import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { ADMIN_NAV } from "@/lib/admin-nav";
import { PortalPage } from "@/components/portal/PortalPage";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";

const STAGES = ["new", "contacted", "qualified", "booked", "converted", "lost"] as const;
type Stage = (typeof STAGES)[number];

interface Lead {
  id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  source: string | null;
  stage: Stage;
  assigned_agent_id: string | null;
  created_at: string;
}

export const Route = createFileRoute("/admin/leads")({
  head: () => ({ meta: [{ title: "Leads — Command Center" }, { name: "robots", content: "noindex" }] }),
  component: () => (
    <ProtectedRoute requiredRole="admin">
      <PortalShell title="Command Center" nav={ADMIN_NAV} tourKey="admin_tour">
        <AdminLeads />
      </PortalShell>
    </ProtectedRoute>
  ),
});

function AdminLeads() {
  const { user } = useAuth();
  const [rows, setRows] = useState<Lead[]>([]);
  const [agents, setAgents] = useState<Array<{ id: string; name: string }>>([]);
  const [stage, setStage] = useState("all");
  const [agent, setAgent] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => { void load(); }, []);

  async function load() {
    const [leadsRes, profRes] = await Promise.all([
      supabase.from("leads").select("id, full_name, email, phone, source, stage, assigned_agent_id, created_at").order("created_at", { ascending: false }),
      supabase.from("profiles").select("id, full_name"),
    ]);
    setRows((leadsRes.data ?? []) as Lead[]);
    setAgents(((profRes.data ?? []) as Array<{ id: string; full_name: string | null }>).map(p => ({ id: p.id, name: p.full_name || "Unnamed" })));
  }

  const agentName = (id: string | null) => id ? (agents.find(a => a.id === id)?.name ?? "—") : "Unassigned";

  const filtered = useMemo(() => rows.filter(r =>
    (stage === "all" || r.stage === stage) &&
    (agent === "all" || r.assigned_agent_id === agent || (agent === "unassigned" && !r.assigned_agent_id)) &&
    (!q || r.full_name.toLowerCase().includes(q.toLowerCase()) || (r.email ?? "").toLowerCase().includes(q.toLowerCase()))
  ), [rows, stage, agent, q]);

  async function setLeadStage(id: string, next: Stage) {
    const { error } = await supabase.from("leads").update({ stage: next }).eq("id", id);
    if (error) return toast.error(error.message);
    setRows(rs => rs.map(r => r.id === id ? { ...r, stage: next } : r));
    await supabase.from("activity_logs").insert({ user_id: user?.id, action: "lead.stage_changed", target_type: "lead", target_id: id, metadata: { stage: next } as any });
  }

  async function reassignLead(id: string, next: string) {
    const value = next === "unassigned" ? null : next;
    const { error } = await supabase.from("leads").update({ assigned_agent_id: value }).eq("id", id);
    if (error) return toast.error(error.message);
    setRows(rs => rs.map(r => r.id === id ? { ...r, assigned_agent_id: value } : r));
    await supabase.from("activity_logs").insert({ user_id: user?.id, action: "lead.reassigned", target_type: "lead", target_id: id, metadata: { assigned_agent_id: value } as any });
  }

  function exportCsv() {
    const header = ["Name", "Email", "Phone", "Source", "Stage", "Strategist", "Created"];
    const lines = filtered.map(r => [r.full_name, r.email ?? "", r.phone ?? "", r.source ?? "", r.stage, agentName(r.assigned_agent_id), r.created_at].map(v => `"${String(v).replace(/"/g, '""')}"`).join(","));
    const blob = new Blob([[header.join(","), ...lines].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <PortalPage eyebrow="Pipeline" title="Leads" intro="Every lead across the agency, with stage, strategist, and source filters." actions={<PrimaryButton onClick={exportCsv}>Export CSV</PrimaryButton>}>
      <div className="flex flex-wrap gap-3 mb-6">
        <Input placeholder="Search name or email" value={q} onChange={e => setQ(e.target.value)} className="max-w-xs" />
        <Select value={stage} onValueChange={setStage}>
          <SelectTrigger className="w-44"><SelectValue placeholder="Stage" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All stages</SelectItem>
            {STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={agent} onValueChange={setAgent}>
          <SelectTrigger className="w-56"><SelectValue placeholder="Strategist" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All strategists</SelectItem>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            {agents.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="bg-white border border-bronze-600/20 rounded-[4px] overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="text-left text-charcoal-500 font-sans uppercase tracking-[0.12em] text-[10px]">
            <tr className="border-b border-bronze-600/20">
              <th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Source</th><th className="px-4 py-3">Stage</th><th className="px-4 py-3">Strategist</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-12 text-center text-charcoal-500">No leads match these filters.</td></tr>
            ) : filtered.map(r => (
              <tr key={r.id} className="border-b border-bronze-600/10 hover:bg-cream-50">
                <td className="px-4 py-3 font-medium text-navy-800">{r.full_name}</td>
                <td className="px-4 py-3 text-charcoal-500">{r.email ?? "—"}</td>
                <td className="px-4 py-3 text-charcoal-500">{r.source ?? "—"}</td>
                <td className="px-4 py-3">
                  <Select value={r.stage} onValueChange={(v) => setLeadStage(r.id, v as Stage)}>
                    <SelectTrigger className="h-8 w-36"><SelectValue /></SelectTrigger>
                    <SelectContent>{STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3">
                  <Select value={r.assigned_agent_id ?? "unassigned"} onValueChange={(v) => reassignLead(r.id, v)}>
                    <SelectTrigger className="h-8 w-48"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {agents.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </PortalPage>
  );
}
